import { useSyncExternalStore } from 'react';

export type DensityPreference = 'auto' | 'comfortable' | 'compact';
export const DENSITY_KEY = 'nexa.density';
// CSS viewport includes OS scaling. Never counteract browser zoom or font size.
function compactMedia() {
  return window.matchMedia
    ? window.matchMedia('(max-height: 820px), (max-width: 1280px)')
    : undefined;
}
const listeners = new Set<() => void>();
let stop: (() => void) | undefined;
function readPreference(): DensityPreference {
  try {
    const value = localStorage.getItem(DENSITY_KEY);
    return value === 'comfortable' || value === 'compact' ? value : 'auto';
  } catch {
    return 'auto';
  }
}
let preference = readPreference();
function isCompact() {
  return preference === 'auto' ? (compactMedia()?.matches ?? false) : preference === 'compact';
}
function snapshot() {
  return `${preference}:${isCompact()}`;
}
function publish() {
  document.documentElement.dataset.density = isCompact() ? 'compact' : 'comfortable';
  for (const listener of listeners) listener();
}
/** One device preference for the shell, every route and portaled dialogs. */
export function startDensity() {
  if (stop) return;
  const media = compactMedia();
  const storageChanged = (event: StorageEvent) => {
    if (event.key === DENSITY_KEY || event.key === null) {
      preference = readPreference();
      publish();
    }
  };
  media?.addEventListener('change', publish);
  window.addEventListener('storage', storageChanged);
  stop = () => {
    media?.removeEventListener('change', publish);
    window.removeEventListener('storage', storageChanged);
  };
  publish();
}
export function setDensity(value: DensityPreference) {
  preference = value;
  try {
    localStorage.setItem(DENSITY_KEY, value);
  } catch {
    /* Session-only fallback. */
  }
  publish();
}
function subscribe(listener: () => void) {
  listeners.add(listener);
  startDensity();
  return () => {
    listeners.delete(listener);
  };
}
export function useDensity() {
  useSyncExternalStore(subscribe, snapshot, () => 'auto:false');
  return { preference, setDensity, compact: isCompact() };
}
/** Reset the browser-lived store between isolated tests. */
export function resetDensityForTest() {
  stop?.();
  stop = undefined;
  listeners.clear();
  preference = readPreference();
  delete document.documentElement.dataset.density;
}
